import Router from 'next/router';
import { Component, createRef } from 'react';
import { Subscription } from 'rxjs';
import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { LightHttp } from '../../services/light-http.service';
import styles from './header-search.component.module.scss';

interface SearchResult {
    slug: string;
    url: string;
    title: string;
    category: string;
}

interface SearchResponse {
    results: SearchResult[];
    query: string;
    count: number;
}

interface HeaderSearchState {
    query: string;
    results: SearchResult[];
    isSearching: boolean;
    isExpanded: boolean;
    activeIndex: number;
}

export class HeaderSearch extends Component<{}, HeaderSearchState> {
    @lazyInject(LightHttp) private lightHttp: LightHttp;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;

    private searchTimeout: ReturnType<typeof setTimeout>;
    private searchSubscription: Subscription;
    private containerRef = createRef<HTMLDivElement>();
    private inputRef = createRef<HTMLInputElement>();
    private navigating = false;

    state: HeaderSearchState = {
        query: '',
        results: [],
        isSearching: false,
        isExpanded: false,
        activeIndex: -1,
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();
    }

    handleClickOutside = (e: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(e.target as Node)) {
            this.collapse();
        }
    };

    expand = () => {
        this.setState({ isExpanded: true }, () => {
            this.inputRef.current?.focus();
        });
    };

    collapse = () => {
        this.setState({ isExpanded: false, results: [], query: '', activeIndex: -1 });
        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();
    };

    onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        this.setState({ query, activeIndex: -1 });

        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();

        if (query.length < 2) {
            this.setState({ results: [], isSearching: false });
            return;
        }

        this.setState({ isSearching: true });

        this.searchTimeout = setTimeout(() => {
            this.searchSubscription = this.lightHttp.get<SearchResponse>(
                this.apiConfig.getFeatureSearchEndpoint(query)
            ).subscribe({
                next: (response) => {
                    this.setState({ results: response.results || [], isSearching: false });
                },
                error: () => {
                    this.setState({ isSearching: false });
                },
            });
        }, 300);
    };

    onKeyDown = (e: React.KeyboardEvent) => {
        const { results, activeIndex } = this.state;

        if (e.key === 'Escape') {
            this.collapse();
            return;
        }

        if (results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ activeIndex: Math.min(activeIndex + 1, results.length - 1) });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ activeIndex: Math.max(activeIndex - 1, 0) });
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            this.navigateTo(results[activeIndex].url);
        }
    };

    navigateTo = (url: string) => {
        if (this.navigating) return;
        this.navigating = true;
        this.collapse();
        Router.push(url).then(() => { this.navigating = false; });
    };

    render() {
        const { query, results, isSearching, isExpanded, activeIndex } = this.state;
        const showDropdown = isExpanded && query.length >= 2 && (results.length > 0 || isSearching || query.length >= 2);

        return (
            <div ref={this.containerRef} className={`${styles.container} ${isExpanded ? styles.expanded : ''}`}>
                {!isExpanded ? (
                    <button className={styles.trigger} onClick={this.expand} aria-label="Search tools">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span className={styles.triggerText}>Search</span>
                    </button>
                ) : (
                    <div className={styles.inputWrapper}>
                        <svg className={styles.inputIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            ref={this.inputRef}
                            className={styles.input}
                            type="text"
                            placeholder="Search tools..."
                            value={query}
                            onChange={this.onSearchInput}
                            onKeyDown={this.onKeyDown}
                        />
                        {isSearching && <div className={styles.spinner} />}
                    </div>
                )}

                {showDropdown && (
                    <div className={styles.dropdown}>
                        {results.length > 0 ? (
                            results.slice(0, 7).map((result, i) => (
                                <div
                                    key={result.slug}
                                    className={`${styles.item} ${i === activeIndex ? styles.active : ''}`}
                                    onClick={() => this.navigateTo(result.url)}
                                    onMouseEnter={() => this.setState({ activeIndex: i })}
                                >
                                    <span className={styles.itemTitle}>{result.title}</span>
                                    <span className={`${styles.itemBadge} ${styles[`cat-${result.category}`] || ''}`}>
                                        {result.category}
                                    </span>
                                </div>
                            ))
                        ) : (
                            !isSearching && (
                                <div className={styles.empty}>No results for &ldquo;{query}&rdquo;</div>
                            )
                        )}
                    </div>
                )}
            </div>
        );
    }
}
